// ===========================================================================
// Market Access – Moonlake: configuration
//
// Everything that decides how the one dataset (market-access-moonlake.data.ts)
// becomes the UI: the filters, the palette, the bucket order, the chart settings
// and — per widget — the aggregation (which rows, grouped by what, stacked by
// what, which measure, aggregated how). The component reads this and hands the
// result to aggregate() (moonlake-aggregate.ts); it holds no grouping logic of
// its own.
// ===========================================================================
import { hsl } from "d3";
import { AggFn, Order, Where } from "./moonlake-aggregate";
import { MoonlakeRecord, RecordSource } from "./market-access-moonlake.data";

type Field = keyof MoonlakeRecord;

// ---------------------------------------------------------------- palette
// The product palette, dark and light. Every colour on this screen comes from
// one of these two lists.
export const PALETTE_DARK = [
  "#0934CC", "#14BDA9", "#1566DE", "#199CD5", "#25D3EB",
  "#2CB339", "#2DADB6", "#7848FF", "#7AD045", "#8A05F3",
  "#9E0000", "#BCD03F", "#CB04DC", "#D9476A", "#DD630B",
  "#E468C2", "#E74028", "#EACD37", "#FA195C", "#FFB039",
];
export const PALETTE_LIGHT = [
  "#8399E5", "#89DED4", "#8AB2EE", "#8CCDE9", "#92E9F5",
  "#95D89C", "#96D6DA", "#BBA4FF", "#BCE8A2", "#C582F9",
  "#CF8080", "#DDE89F", "#E581ED", "#ECA3B5", "#EEB185",
  "#F1B3E0", "#F29F93", "#F4E69B", "#FC8CAD", "#FFD79C",
];

// ------------------------------------------------------ biologics buckets
// Two kinds of bucket, told apart by the record's `bucket_type`:
//
//   "Line"   — a line of therapy, "<n>L" for ANY n ≥ 1 (1L, 2L, 3L, 4L, … 12L …).
//              Which lines exist is read from the data, never listed here.
//   "Status" — everything else, listed in STATUS_BUCKETS.
//
// Ungrouped, every line keeps its own band: 1L, 2L, 3L, 4L, … Grouped, lines
// from LINE_GROUP_FROM up fold into one LINE_GROUP_KEY band: 1L, 2L, 3L+.
export const LINE_BUCKET_TYPE = "Line";
export const LINE_GROUP_FROM = 3;
export const LINE_GROUP_KEY = `${LINE_GROUP_FROM}L+`;

/** Status buckets, in legend order (after the lines). */
export const STATUS_BUCKETS = [
  "New 1L - High Conf",
  "New 1L - Me",
  "No Coverage",
  "Unspecified",
  "Unspecified - ST",
];

/** Buckets that count as Not Covered in the Covered vs. Not Covered view. */
const NOT_COVERED_BUCKETS = new Set(["No Coverage", "Unspecified", "Unspecified - ST"]);

/** The line number of a record's bucket, or null when it is not a line bucket. */
export function lineNumber(r: Pick<MoonlakeRecord, "bucket" | "bucket_type">): number | null {
  if (r.bucket_type !== LINE_BUCKET_TYPE) return null;
  const m = /^(\d+)\s*L$/i.exec(String(r.bucket).trim());
  return m ? Number(m[1]) : null;
}

export const lineKey = (n: number): string => `${n}L`;

/** Distinct line numbers present in `records`, ascending. */
export function linesIn(records: MoonlakeRecord[]): number[] {
  const set = new Set<number>();
  records.forEach((r) => {
    const n = lineNumber(r);
    if (n !== null) set.add(n);
  });
  return [...set].sort((a, b) => a - b);
}

// Colours. 1L and 2L have their own; 3L and deeper share one green (the
// palette's #2CB339, hue 126°) in a ramp generated for however many deep lines
// the data has — lightest at 3L, darkest at the deepest. Grouped, the single 3L+
// band is that same ramp as a gradient.
const LINE_COLORS: Record<number, string> = {
  1: "#14BDA9",
  2: "#7848FF",
};
const DEEP_RAMP = { hue: 125.8, sat: 0.69, light: 0.62, dark: 0.26 };

const STATUS_COLORS: Record<string, string> = {
  "New 1L - High Conf": "#FFB039",
  "New 1L - Me": "#BCD03F",
  "No Coverage": "#D9476A",
  Unspecified: "#8399E5",
  "Unspecified - ST": "#0934CC",
};

const rampColor = (t: number): string =>
  hsl(DEEP_RAMP.hue, DEEP_RAMP.sat, DEEP_RAMP.light - t * (DEEP_RAMP.light - DEEP_RAMP.dark))
    .formatHex()
    .toUpperCase();

/** The grouped 3L+ band's gradient (top → bottom): the deep-line ramp end to end. */
export const GROUPED_3L_GRADIENT: [string, string] = [rampColor(0), rampColor(1)];

/** Colour per stack key, for the lines actually present plus every status. */
export function bucketColors(lines: number[]): Record<string, string> {
  const colors: Record<string, string> = { ...STATUS_COLORS };
  const deep = lines.filter((n) => n >= LINE_GROUP_FROM);
  lines.forEach((n) => {
    if (LINE_COLORS[n]) {
      colors[lineKey(n)] = LINE_COLORS[n];
    } else {
      const i = deep.indexOf(n);
      colors[lineKey(n)] = rampColor(deep.length > 1 ? i / (deep.length - 1) : 0.5);
    }
  });
  colors[LINE_GROUP_KEY] = rampColor(0.5);
  return colors;
}

export const BUCKET_SHORT_LABELS: Record<string, string> = {
  "New 1L - High Conf": "New 1L-HC",
  "New 1L - Me": "New 1L-Me",
  "No Coverage": "No Cov.",
  Unspecified: "Unspec.",
  "Unspecified - ST": "Unspec.-ST",
};

export const COVERED_KEYS = ["Covered", "Not Covered"];
export const COVERED_COLORS: Record<string, string> = {
  Covered: "#14BDA9",
  "Not Covered": "#7848FF",
};

// --------------------------------------------------------------- show by
export type ShowBy = "group3l" | "covered";

export const SHOW_BY_OPTIONS: { value: ShowBy; title: string }[] = [
  { value: "group3l", title: "Group 3L & Above as 3L+" },
  { value: "covered", title: "Covered vs. Not Covered" },
];

/**
 * The stack key a record lands in for a view — the `stackMap` aggregate()
 * applies. Covered vs. Not Covered reads the status; the Biologics Count view
 * keeps each line as "<n>L", folding lines ≥ 3 into "3L+" when grouped. A line
 * bucket that does not parse is dropped (null) rather than mis-stacked.
 */
export function stackKeyFor(r: MoonlakeRecord, showBy: ShowBy, grouped: boolean): string | null {
  if (showBy === "covered") {
    return NOT_COVERED_BUCKETS.has(r.bucket) ? "Not Covered" : "Covered";
  }
  if (r.bucket_type !== LINE_BUCKET_TYPE) return r.bucket;
  const n = lineNumber(r);
  if (n === null) return null;
  return grouped && n >= LINE_GROUP_FROM ? LINE_GROUP_KEY : lineKey(n);
}

/**
 * Legend keys (left → right) for a view, given the line numbers present:
 * ungrouped 1L, 2L, 3L, 4L, …; grouped 1L, 2L, 3L+; then the statuses.
 */
export function legendKeysFor(showBy: ShowBy, grouped: boolean, lines: number[]): string[] {
  if (showBy === "covered") return COVERED_KEYS;
  const lineKeys = grouped
    ? [
        ...lines.filter((n) => n < LINE_GROUP_FROM).map(lineKey),
        ...(lines.some((n) => n >= LINE_GROUP_FROM) ? [LINE_GROUP_KEY] : []),
      ]
    : lines.map(lineKey);
  return [...lineKeys, ...STATUS_BUCKETS];
}

/**
 * Stack keys (bottom → top): the legend order, except that the deep lines
 * stack deepest first (the darkest green on 2L, 3L on top), so the green runs
 * dark to light up the bar — the same direction as the grouped 3L+ gradient.
 */
export function stackKeysFor(showBy: ShowBy, grouped: boolean, lines: number[]): string[] {
  const keys = legendKeysFor(showBy, grouped, lines);
  if (showBy === "covered" || grouped) return keys;
  const isDeep = (k: string) => {
    const m = /^(\d+)L$/.exec(k);
    return !!m && Number(m[1]) >= LINE_GROUP_FROM;
  };
  const deep = keys.filter(isDeep).reverse();
  let i = 0;
  return keys.map((k) => (isDeep(k) ? deep[i++] : k));
}

// ------------------------------------------------------- chart settings
export type XAxis = "drug" | "channel" | "snapshot";
export type YAxis = "pct" | "abs";

export interface XAxisOption {
  key: XAxis;
  /** Settings popover label. */
  label: string;
  /** Axis title under the chart. */
  axisLabel: string;
  /** The record field one bar is drawn per. */
  groupBy: Field;
  /** Bar order. */
  order: Order<MoonlakeRecord>;
  /**
   * Which snapshots the bars read. Lives are a point-in-time count, so summing
   * them across snapshots would double count: every axis except Snapshot reads
   * only the latest selected one.
   */
  snapshots: "latest" | "all";
}

export const X_AXIS_OPTIONS: XAxisOption[] = [
  { key: "drug", label: "Drug", axisLabel: "DrugClean", groupBy: "drug", order: "alpha", snapshots: "latest" },
  { key: "channel", label: "Channel", axisLabel: "Channel", groupBy: "channel", order: "alpha", snapshots: "latest" },
  {
    key: "snapshot",
    label: "Snapshot",
    axisLabel: "Snapshot",
    groupBy: "snapshot",
    order: { by: "snapshot_dt" },
    snapshots: "all",
  },
];

export const Y_AXIS_OPTIONS: { key: YAxis; label: string; axisTitle: string }[] = [
  { key: "pct", label: "% of lives", axisTitle: "Sum of Lives" },
  { key: "abs", label: "No. of Lives", axisTitle: "No. of Lives" },
];

// ---------------------------------------------------------------- filters
/**
 *   "list"    — a flat multi-select (or single-select) over one record field;
 *   "tree"    — a nested multi-select over several fields, one per level; its
 *               values are the leaves (one per distinct path);
 *   "display" — a chip that states what is in scope and cannot be changed.
 */
export type FilterKind = "list" | "tree" | "display";

export interface TreeLevel {
  field: Field;
  label: string;
  /** Shown muted after the node's name, e.g. "(PBM)". */
  tag?: boolean;
}

export interface MoonlakeFilterDef {
  /** State key; for "list" and "display" also the record field filtered. */
  key: string;
  label: string;
  kind: FilterKind;
  single?: boolean;
  /** Option order ("list"). Alphabetical when omitted. */
  order?: Order<MoonlakeRecord>;
  /** Levels, top to bottom ("tree"). */
  levels?: TreeLevel[];
  /** Shown in a "display" chip's dropdown. */
  note?: string;
}

/**
 * Every filter this screen offers, in strip order. The order is also the
 * cascade: each chip's values are the ones that exist under the chips before it
 * (see cascadeFilters() in the component), as on DPM-Static. Date is not here —
 * it is a quarter range rather than a list of values (see the date section).
 *
 * Channel is the X axis of the coverage cards, not a chip: on Market Access the
 * Payer drawer follows MMIT's hierarchy (PBM, Controller, Plan, Formulary).
 */
export const MOONLAKE_FILTERS: MoonlakeFilterDef[] = [
  {
    key: "payer",
    label: "Payer Hierarchy",
    kind: "tree",
    levels: [
      { field: "pbm", label: "PBM", tag: true },
      { field: "controller", label: "Controller" },
      { field: "plan", label: "Plan" },
      { field: "formulary", label: "Formulary" },
    ],
  },
  {
    key: "therapeutic_class",
    label: "Therapeutic Class",
    kind: "display",
    note: "The class in scope. The competitive set is fixed for this release.",
  },
  {
    key: "drug_class",
    label: "Class of Drug",
    kind: "list",
    order: ["TNFs", "IL17", "IL23", "JAKs", "Other"],
  },
  { key: "drug", label: "Drug Clean", kind: "list" },
  { key: "indication", label: "Indication", kind: "list" },
  { key: "territory", label: "Territory", kind: "list" },
];

// ------------------------------------------------------------------- date
/**
 * The Date chip selects a contiguous range of quarters (start to end). The
 * coverage panels are MMIT formulary snapshots, so they never sum across
 * snapshots: the Drug and Channel views read the latest snapshot on or before
 * the period's end; the Snapshot view shows every snapshot in the period.
 */
export const DATE_LABEL = "Date";

/** The quarter a fresh visit (and Reset) opens on; the latest quarter in the data if absent. */
export const DEFAULT_QUARTER = "2026-Q2";

export interface Quarter {
  /** "2026-Q2" */
  key: string;
  /** "Q2 2026" */
  label: string;
  /** First day, ISO. */
  start: string;
  /** Last month's "-31": an inclusive upper bound for ISO string comparison. */
  end: string;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

export function quarterKeyOf(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
}

export function quarterFromKey(key: string): Quarter {
  const [y, q] = key.split("-Q").map(Number);
  return {
    key,
    label: `Q${q} ${y}`,
    start: `${y}-${pad2(q * 3 - 2)}-01`,
    end: `${y}-${pad2(q * 3)}-31`,
  };
}

/** Every quarter from the one holding `minIso` to the one holding `maxIso`, contiguous. */
export function quartersBetween(minIso: string, maxIso: string): Quarter[] {
  if (!minIso || !maxIso) return [];
  const out: Quarter[] = [];
  let [y, q] = quarterKeyOf(minIso).split("-Q").map(Number);
  const last = quarterKeyOf(maxIso);
  for (let guard = 0; guard < 400; guard++) {
    const key = `${y}-Q${q}`;
    out.push(quarterFromKey(key));
    if (key === last) break;
    q === 4 ? ((q = 1), y++) : q++;
  }
  return out;
}

// ---------------------------------------------------------------- pages
export interface MoonlakePage {
  key: string;
  title: string;
}

export const MOONLAKE_PAGES: MoonlakePage[] = [
  { key: "coverage", title: "Coverage by Payer/PBM and Drug" },
  { key: "access", title: "Access & Coverage" },
  { key: "competitive", title: "Competitive Position" },
  // Hidden for now — uncomment to bring the pages back.
  // { key: "rwe", title: "Real-World Evidence" },
  // { key: "um", title: "Utilization Management" },
];

// --------------------------------------------------------------- widgets
/**
 * One chart widget, as configuration. The component turns this, plus the card's
 * own settings (Show by, grouping, X/Y axis), into an AggSpec:
 *
 *   where   = widget.where  (+ the latest snapshot, per the X axis)
 *   groupBy = X axis' field          order  = X axis' order
 *   stackBy = widget.stackBy         stackMap / stackKeys = the Show-by view
 *   measure = widget.measure         fn     = widget.fn
 */
export interface StackedWidgetConfig {
  id: string;
  title: string;
  page: string;
  /** Rows this widget reads, before the filters' own narrowing. */
  where: Where<MoonlakeRecord>;
  stackBy: Field;
  measure: Field;
  fn: AggFn;
  /** Unit noun for tooltips and the waterfall summary. */
  unit: string;
  /** Default X axis — the card's Settings popover can change it. */
  xAxis: XAxis;
  /** Default Y axis. */
  yAxis: YAxis;
  /** Optional label before the legend items; none when omitted. */
  legendTitle?: string;
}

export const COVERAGE_WIDGETS: StackedWidgetConfig[] = [
  {
    id: "hs",
    title: "HS",
    page: "coverage",
    where: { indication: "HS" },
    stackBy: "bucket",
    measure: "lives",
    fn: "sum",
    unit: "lives",
    xAxis: "channel",
    yAxis: "pct",
  },
  {
    id: "psa",
    title: "PsA",
    page: "coverage",
    where: { indication: "PsA" },
    stackBy: "bucket",
    measure: "lives",
    fn: "sum",
    unit: "lives",
    xAxis: "drug",
    yAxis: "pct",
  },
  {
    id: "pso",
    title: "PsO",
    page: "coverage",
    where: { indication: "PsO" },
    stackBy: "bucket",
    measure: "lives",
    fn: "sum",
    unit: "lives",
    xAxis: "drug",
    yAxis: "pct",
  },
];

// ===========================================================================
// Page 2 — Access & Coverage
// ===========================================================================
/** The brand the page measures access for; every competitor is read against it. */
export const FOCUS_DRUG = "Bimzelx";

/** Covered: any bucket but No Coverage / Unspecified / Unspecified - ST. */
export const isCovered = (r: Pick<MoonlakeRecord, "bucket">): boolean =>
  !NOT_COVERED_BUCKETS.has(r.bucket);

/** No step: covered at first line — no prior therapy has to fail first. */
const NO_STEP_BUCKETS = new Set(["1L", "New 1L - High Conf", "New 1L - Me"]);

// ------------------------------------------------------------------ KPIs
/** What a KPI card shows, whichever page it is on. */
export interface KpiTile {
  id: string;
  /** Card label; also the help entry's header. */
  label: string;
  color: string;
  icon: "pulse" | "check";
}

export interface KpiConfig extends KpiTile {
  /** Lives that count toward the rate; the denominator is all of the focus brand's lives. */
  numerator: (r: MoonlakeRecord) => boolean;
}

/**
 * Lives-weighted rates for the focus brand, from the MMIT file: the latest
 * snapshot on or before the period end, every other filter applied.
 */
export const ACCESS_KPIS: KpiConfig[] = [
  {
    id: "national-access",
    label: "Lives-Weighted National Access Rate (% Covered Lives)",
    color: "#2CB339",
    icon: "pulse",
    numerator: isCovered,
  },
  {
    id: "no-step",
    label: "No-Step Access Rate",
    color: "#FFB039",
    icon: "check",
    numerator: (r) => NO_STEP_BUCKETS.has(r.bucket),
  },
];

// ------------------------------------------------------- series widgets
/**
 * Where a widget's rows come from — the `source` of the page's records (see
 * RecordSource): "mmit" is already narrowed by every chip and Date; any other
 * source is narrowed by what its SourceScope says it honours.
 */
export type SeriesSource = RecordSource;

/** Which rows of a non-MMIT source a widget reads, and which filters narrow them. */
export interface SourceScope {
  source: SeriesSource;
  /**
   * Non-MMIT sources: filter key → this source's field. A value the filter does
   * not list is not narrowed by it (e.g. "[Brand X]" against Drug Clean).
   */
  honors?: Record<string, string>;
  /** The records carry the full payer path (PBM › Controller › Plan › Formulary): Payer applies as is. */
  honorsPayer?: boolean;
  /** Payer applies at channel level: the channels of the selected formularies. */
  payerAsChannel?: string;
  /** The Date range applies to this ISO field (claims recompute over the whole range). */
  periodField?: string;
}

export interface SeriesXOption {
  key: string;
  label: string;
  axisLabel: string;
  groupBy: string;
  order: Order<any>;
  /** MMIT only: read the latest snapshot ("latest") or every one in the period ("all"). */
  snapshots?: "latest" | "all";
}

export interface SeriesYOption {
  /** "pct" draws a 0–100% axis; any other key a value axis. */
  key: string;
  label: string;
  axisTitle: string;
  /**
   * How the value is worked out when it is not the widget's count / share:
   *   "ratio" — measure ÷ denominator (an average, e.g. months per patient);
   *   "index" — the widget's `index.focus` records' rate over every other
   *             record's (measure ÷ denominator each side); 1 is parity.
   */
  mode?: "ratio" | "index";
  /** Overrides the widget's measure / denominator for this view. */
  measure?: string;
  denominator?: string;
  /** Decimal places for values in this view; compact counts when omitted. */
  decimals?: number;
  /** Unit noun for this view, when it differs from the widget's. */
  unit?: string;
}

export interface SeriesWidgetConfig extends SourceScope {
  id: string;
  title: string;
  page: string;
  chart: "bar" | "line";
  /** Rows this widget reads, before the filters' own narrowing. */
  where: Where<any>;
  /** One bar / line per value of this field, in `series` order (those present). Omitted: one series, `series[0]`. */
  seriesBy?: string;
  series: string[];
  /** Legend text where it differs from the value. */
  seriesLabels?: Record<string, string>;
  /** Tooltip suffix per series. */
  seriesNotes?: Record<string, string>;
  colours: Record<string, string>;
  /** Summed for the value ("abs") and the share's numerator ("pct"). */
  measure: string;
  /** The share's denominator field; `measure` when omitted. */
  denominator?: string;
  /** Records counted toward the value — e.g. covered buckets. All when omitted. */
  numerator?: (r: any) => boolean;
  /** What the % view is a share of: the cell (per series) or the whole group. */
  pctOf: "series" | "group";
  xAxes: SeriesXOption[];
  yAxes: SeriesYOption[];
  xAxis: string;
  yAxis: string;
  /** A Show-by select narrowing one field; "All" first. */
  showBy?: { field: string; allLabel: string; order: string[] };
  /** The "index" Y view's focus: these records are measured against all the rest. */
  index?: { focus: (r: any) => boolean };
  unit: string;
}

const COVERED_LIVES_Y: SeriesYOption[] = [
  { key: "pct", label: "% of covered lives", axisTitle: "% Covered Lives" },
  { key: "abs", label: "No. of covered lives", axisTitle: "Covered Lives" },
];

export const ACCESS_WIDGETS: SeriesWidgetConfig[] = [
  {
    id: "access-parity",
    title: "Access Parity vs. Competitor, by Channel",
    page: "access",
    source: "mmit",
    chart: "bar",
    where: { drug: [FOCUS_DRUG, "Cosentyx", "Tremfya", "Taltz"] },
    seriesBy: "drug",
    series: [FOCUS_DRUG, "Cosentyx", "Tremfya", "Taltz"],
    seriesLabels: { [FOCUS_DRUG]: `${FOCUS_DRUG} (% Covered Lives)` },
    colours: { [FOCUS_DRUG]: "#14BDA9", Cosentyx: "#7848FF", Tremfya: "#D9476A", Taltz: "#FFB039" },
    measure: "lives",
    numerator: isCovered,
    pctOf: "series",
    xAxes: [
      { key: "channel", label: "Channel", axisLabel: "Channel", groupBy: "channel", order: "alpha", snapshots: "latest" },
      {
        key: "snapshot",
        label: "Snapshot",
        axisLabel: "Snapshot",
        groupBy: "snapshot",
        order: { by: "snapshot_dt" },
        snapshots: "all",
      },
    ],
    yAxes: COVERED_LIVES_Y,
    xAxis: "channel",
    yAxis: "pct",
    unit: "lives",
  },
  {
    id: "analog-launch",
    title: "Analog Launch Coverage Curve",
    page: "access",
    source: "analog",
    chart: "line",
    where: {},
    seriesBy: "brand",
    series: ["[Brand X]", "Bimzelx", "Skyrizi", "Taltz", "Cosentyx", "Tremfya"],
    seriesNotes: { "[Brand X]": "Forecast" },
    colours: {
      "[Brand X]": "#14BDA9",
      Bimzelx: "#7848FF",
      Skyrizi: "#FFB039",
      Taltz: "#BCD03F",
      Cosentyx: "#D9476A",
      Tremfya: "#1566DE",
    },
    measure: "covered_lives",
    denominator: "lives",
    pctOf: "series",
    xAxes: [
      {
        key: "month",
        label: "Months since launch",
        axisLabel: "Months Since Launch",
        groupBy: "month_label",
        order: { by: "month" },
      },
    ],
    yAxes: COVERED_LIVES_Y,
    xAxis: "month",
    yAxis: "pct",
    // Launch history: Date and Payer do not apply; brands answer Drug Clean.
    honors: { indication: "indication", drug_class: "drug_class", drug: "brand" },
    unit: "lives",
  },
  {
    id: "addressable-market",
    title: "Access-Adjusted Addressable Market, by Payer Channel",
    page: "access",
    source: "addressable",
    chart: "bar",
    where: {},
    seriesBy: "access",
    series: ["Access-Adjusted Addressable", "Addressable, Restricted Access"],
    colours: { "Access-Adjusted Addressable": "#2CB339", "Addressable, Restricted Access": "#6B7580" },
    measure: "patients",
    pctOf: "group",
    xAxes: [
      { key: "channel", label: "Channel", axisLabel: "Payer Channel", groupBy: "channel", order: "alpha" },
      { key: "territory", label: "Territory", axisLabel: "Territory", groupBy: "territory", order: "alpha" },
      { key: "period", label: "Quarter", axisLabel: "Quarter", groupBy: "period", order: { by: "period_dt" } },
    ],
    yAxes: [
      { key: "abs", label: "No. of patients", axisTitle: "Addressable Patients" },
      { key: "pct", label: "% of addressable", axisTitle: "% of Addressable Patients" },
    ],
    xAxis: "channel",
    yAxis: "abs",
    showBy: { field: "position", allLabel: "All Positions", order: ["1L", "2L", "3L+"] },
    honors: { indication: "indication", territory: "territory" },
    payerAsChannel: "channel",
    periodField: "period_dt",
    unit: "patients",
  },
];

// ===========================================================================
// Page 3 — Competitive Position
// ===========================================================================
/**
 * A bucket's access position as a rank — lower is better. First line (and the
 * New 1L statuses) is 1, "<n>L" is n; Unspecified sits at 2 (covered, line not
 * stated), Unspecified - ST at 3 (a step edit of unstated depth) and No Coverage
 * below every line.
 */
export const NO_COVERAGE_RANK = 99;
const STATUS_RANK: Record<string, number> = {
  "New 1L - High Conf": 1,
  "New 1L - Me": 1,
  Unspecified: 2,
  "Unspecified - ST": 3,
  "No Coverage": NO_COVERAGE_RANK,
};

export function positionRank(r: Pick<MoonlakeRecord, "bucket" | "bucket_type">): number {
  const n = lineNumber(r);
  if (n !== null) return n;
  return STATUS_RANK[r.bucket] ?? NO_COVERAGE_RANK;
}

export const PARITY_BETTER = "At Parity Or Better";
export const PARITY_WORSE = "Worse Than Parity";
export const PARITY_KEYS = [PARITY_BETTER, PARITY_WORSE];
export const PARITY_COLORS: Record<string, string> = {
  [PARITY_BETTER]: "#14BDA9",
  [PARITY_WORSE]: "#7848FF",
};

export interface ParityWidgetConfig {
  id: string;
  title: string;
  page: string;
  xAxes: { key: string; label: string; axisLabel: string; groupBy: Field; order: Order<MoonlakeRecord> }[];
  xAxis: string;
  yAxis: YAxis;
  /**
   * Filters left out of the parity reference. The competitor set is fixed for the
   * release, so narrowing Drug Clean / Class of Drug chooses which bars show —
   * it does not move the bar they are measured against.
   */
  referenceIgnores: string[];
  unit: string;
}

/**
 * Each drug's lives split by where it stands against the competitor set on the
 * same formulary: the reference is the lives-weighted median position of every
 * product on that formulary (same snapshot and indication). At or above it is
 * "At Parity Or Better", below it "Worse Than Parity". Formulary snapshots only:
 * the latest one on or before the period end.
 */
export const COMPETITIVE_PARITY: ParityWidgetConfig = {
  id: "relative-position",
  title: "Relative Tier/Status Position vs. Competitor Set",
  page: "competitive",
  xAxes: [
    { key: "drug", label: "Drug", axisLabel: "DrugClean", groupBy: "drug", order: "alpha" },
    { key: "drug_class", label: "Class of Drug", axisLabel: "Class of Drug", groupBy: "drug_class", order: ["TNFs", "IL17", "IL23", "JAKs", "Other"] },
    { key: "channel", label: "Channel", axisLabel: "Channel", groupBy: "channel", order: "alpha" },
  ],
  xAxis: "drug",
  yAxis: "pct",
  referenceIgnores: ["drug", "drug_class"],
  unit: "lives",
};

/**
 * Step-through requirements: one row per plan / formulary for the drug picked
 * in the card — its lives (MMIT, every filter applied), coverage, access
 * position and the step text as MMIT states it — fields of page 3's records
 * (market-access-moonlake-competitive-position.json).
 */
export const STEP_TABLE = {
  id: "step-through",
  title: "Step-Through Requirements by Plan, for a Selected Drug",
  page: "competitive",
  defaultDrug: FOCUS_DRUG,
};

// ===========================================================================
// Page 4 — Real-World Evidence
// ===========================================================================
// Read from page 4's claims ("rx") and switching ("switch") records. They carry
// the payer path / channel, territory, indication and quarter, so the filters
// narrow them as they do the MMIT records. The focus brand's widgets leave out
// Drug Clean and Class of Drug: the competitor set is fixed for the release.

/** Claims records, scoped by every filter but the drug chips. */
const RX_SCOPE: SourceScope = {
  source: "rx",
  honors: { indication: "indication", territory: "territory" },
  honorsPayer: true,
  periodField: "period_dt",
};

/** Switching records: no plan id, so Payer applies at channel level. */
const SWITCH_SCOPE: SourceScope = {
  source: "switch",
  honors: { indication: "indication", territory: "territory" },
  payerAsChannel: "channel",
  periodField: "period_dt",
};

/**
 * Net switching for the focus brand: patients switching to it less those
 * switching away, over every switch in scope — above the target means it wins
 * more patients from competitors than it loses to them.
 */
export interface SwitchKpiConfig extends KpiTile, SourceScope {
  focus: string;
  /** Percent; the figure turns `missColor` below it. */
  target: number;
  missColor: string;
}

export const RWE_KPIS: SwitchKpiConfig[] = [
  {
    ...SWITCH_SCOPE,
    id: "win-loss",
    label: "Competitive Win/Loss (Switching) Rate",
    color: "#2CB339",
    missColor: "#D9476A",
    icon: "pulse",
    focus: FOCUS_DRUG,
    target: 0,
  },
];

/**
 * Relative Payer Access Index: per plan, the focus brand's approval rate
 * (approved ÷ requested claims) over the competitor set's on the same plan.
 * 1 is parity; below 1 the plan is harder on the brand than on its competitors.
 */
export const RWE_ACCESS_INDEX: SeriesWidgetConfig = {
  ...RX_SCOPE,
  id: "relative-access-index",
  title: "Relative Payer Access Index (Real-World Evidence)",
  page: "rwe",
  chart: "bar",
  where: {},
  series: ["Relative Access Index"],
  colours: { "Relative Access Index": "#FFB039" },
  measure: "approved",
  denominator: "requests",
  index: { focus: (r) => r.drug === FOCUS_DRUG },
  pctOf: "series",
  xAxes: [
    { key: "plan", label: "Plan", axisLabel: "Plan", groupBy: "payer_plan", order: "alpha" },
    { key: "pbm", label: "PBM", axisLabel: "PBM", groupBy: "pbm", order: "alpha" },
    { key: "channel", label: "Channel", axisLabel: "Channel", groupBy: "channel", order: "alpha" },
  ],
  yAxes: [
    {
      key: "index",
      label: "Relative access index",
      axisTitle: "Relative Access Index",
      mode: "index",
      decimals: 2,
      unit: "index",
    },
  ],
  xAxis: "plan",
  yAxis: "index",
  unit: "index",
};

/**
 * Plan-Level Access/Restriction Index: per plan, the share of the focus brand's
 * claims that hit a restriction (rejected, PA or step edit), highest first,
 * beside the plan's MMIT coverage of the brand.
 */
export const RWE_RESTRICTION_TABLE = {
  ...RX_SCOPE,
  id: "restriction-index",
  title: "Plan-Level Access/Restriction Index (Real-World Evidence)",
  page: "rwe",
  focus: FOCUS_DRUG,
  /** MMIT status reads the latest snapshot with every filter but these. */
  statusIgnores: ["drug", "drug_class"],
};

/** A stacked share chart over a non-MMIT source, drawn by the waterfall chart (as page 1). */
export interface ShareWidgetConfig extends SourceScope {
  id: string;
  title: string;
  page: string;
  /** One band per value of this field. */
  stackBy: string;
  /** Band order: these first, then the rest alphabetically. */
  stackFirst: string[];
  /** A fixed colour per band; without it, drugColours() over the source's values. */
  colours?: Record<string, string>;
  /** The Show-by select: which measure the bands sum. */
  measures: { key: string; title: string }[];
  xAxes: SeriesXOption[];
  xAxis: string;
  yAxis: YAxis;
  yAxes: { key: YAxis; label: string; axisTitle: string }[];
  unit: string;
}

export const RWE_RX_SHARE: ShareWidgetConfig = {
  source: "rx",
  honors: { indication: "indication", territory: "territory", drug: "drug", drug_class: "drug_class" },
  honorsPayer: true,
  periodField: "period_dt",
  id: "rx-share",
  title: "NBRx/TRx Market Share vs. Competitor Set (Real-World Evidence)",
  page: "rwe",
  stackBy: "drug",
  stackFirst: [FOCUS_DRUG],
  measures: [
    { key: "nbrx", title: "NBRx" },
    { key: "trx", title: "TRx" },
  ],
  xAxes: [
    { key: "period", label: "Quarter", axisLabel: "Quarter", groupBy: "period", order: { by: "period_dt" } },
    { key: "channel", label: "Channel", axisLabel: "Channel", groupBy: "channel", order: "alpha" },
    { key: "territory", label: "Territory", axisLabel: "Territory", groupBy: "territory", order: "alpha" },
  ],
  xAxis: "period",
  yAxis: "abs",
  yAxes: [
    { key: "abs", label: "No. of prescriptions", axisTitle: "Prescriptions" },
    { key: "pct", label: "% market share", axisTitle: "Market Share" },
  ],
  unit: "Rx",
};

/** Band colours for the share chart: the focus brand teal, the rest round the palette. */
export function drugColours(drugs: string[]): Record<string, string> {
  const rest = PALETTE_DARK.filter((c) => c !== "#14BDA9");
  const out: Record<string, string> = {};
  drugs.forEach((d, i) => (out[d] = d === FOCUS_DRUG ? "#14BDA9" : rest[i % rest.length]));
  return out;
}

/**
 * Competitor switching: per from → to pair, the average months patients spent
 * on the first drug before switching, or how many switched.
 */
export const RWE_SWITCHING: SeriesWidgetConfig = {
  ...SWITCH_SCOPE,
  id: "competitor-switching",
  title: "Competitor Switch Rate / Time-on-Therapy Before Switch (Pre-Launch)",
  page: "rwe",
  chart: "bar",
  where: {},
  series: ["Switching"],
  colours: { Switching: "#2CB339" },
  measure: "switched",
  pctOf: "series",
  xAxes: [{ key: "pair", label: "Switch", axisLabel: "Switch", groupBy: "pair", order: "total-desc" }],
  yAxes: [
    {
      key: "months",
      label: "Avg. months on therapy before switch",
      axisTitle: "Avg. Months on Therapy",
      mode: "ratio",
      measure: "months_on_therapy",
      denominator: "switched",
      decimals: 1,
      unit: "months",
    },
    { key: "abs", label: "No. of switching patients", axisTitle: "Patients Switching", unit: "patients" },
  ],
  xAxis: "pair",
  yAxis: "months",
  unit: "patients",
};

// ------------------------------------------------------------- pagination
/** Rows per page a grid offers (Page Size), and the one it opens on. */
export const TABLE_PAGE_SIZES = [5, 10, 20, 50];
export const TABLE_PAGE_SIZE = 10;

// ===========================================================================
// Page 5 — Utilization Management
// ===========================================================================
/** One column of a formulary table: the cell's key in the row, its heading. */
export interface TableColumn {
  key: string;
  header: string;
  align?: "center";
  /** Excel column width. */
  width: number;
}

/**
 * A table with one row per formulary for a picked drug — as page 3's
 * step-through table: the MMIT records of the latest snapshot on or before the
 * period end, every filter applied, largest lives first. Its cells come from
 * any one of the formulary's records (they agree on the formulary-level
 * fields), so a new table is a new entry here.
 */
export interface FormularyTableConfig {
  id: string;
  title: string;
  page: string;
  defaultDrug: string;
  columns: TableColumn[];
  cells: (r: any) => Record<string, string>;
  /** Hover text for a row (e.g. what deviates). */
  rowTitle?: (r: any) => string;
  /** In the page's two-up row: the narrower or the wider card. */
  layout: "side" | "wide";
}

const planCell = (r: any): string => `${r.controller} — ${r.plan} / ${r.formulary}`;

/** How a formulary differs from its parent PBM's national formulary, one fact per entry. */
export function nationalDeviations(r: any): string[] {
  const out: string[] = [];
  if (r.coverage !== r.national_coverage) out.push(`Coverage: ${r.coverage} (national: ${r.national_coverage})`);
  if (r.access_position !== r.national_position) {
    out.push(`Access position: ${r.access_position} (national: ${r.national_position})`);
  }
  if (r.quantity_limit !== r.national_quantity_limit) {
    out.push(`Quantity limit: ${r.quantity_limit} (national: ${r.national_quantity_limit})`);
  }
  return out;
}

export const UM_TABLES: FormularyTableConfig[] = [
  {
    id: "formulary-deviation",
    title: "PBM-National vs. Custom/Downstream Formulary Deviation",
    page: "um",
    defaultDrug: FOCUS_DRUG,
    columns: [
      { key: "plan", header: "Plan", width: 44 },
      { key: "pbm", header: "Parent PBM", width: 20 },
      { key: "deviates", header: "Deviates From National?", align: "center", width: 22 },
    ],
    cells: (r) => ({
      plan: planCell(r),
      pbm: r.pbm,
      deviates: nationalDeviations(r).length ? "Yes" : "No",
    }),
    rowTitle: (r) => nationalDeviations(r).join("\n") || "Same as the PBM's national formulary",
    layout: "side",
  },
  {
    id: "um-detail",
    title: "Utilization Management Detail, Merged with Coverage Status",
    page: "um",
    defaultDrug: FOCUS_DRUG,
    columns: [
      { key: "plan", header: "Plan", width: 44 },
      { key: "coverage", header: "Coverage", width: 22 },
      { key: "quantity_limit", header: "Quantity Limit", width: 20 },
      { key: "step", header: "Step Therapy Detail", width: 48 },
    ],
    cells: (r) => ({
      plan: planCell(r),
      coverage: r.coverage,
      quantity_limit: r.quantity_limit,
      step: r.step_text,
    }),
    layout: "wide",
  },
];

/**
 * Claims rejections (or abandonments) split by reason, per channel —
 * market-wide, so the drug chips do not apply. 100% stacked by default.
 */
export const UM_REJECTIONS: ShareWidgetConfig = {
  source: "reject",
  honors: { indication: "indication", territory: "territory" },
  payerAsChannel: "channel",
  periodField: "period_dt",
  id: "rejection-reasons",
  title: "Rejection/Abandonment Rate by Reason (Market-Wide)",
  page: "um",
  stackBy: "reason",
  stackFirst: ["PA Required", "Step Therapy", "Quantity Limit", "All Other"],
  colours: {
    "PA Required": "#D9476A",
    "Step Therapy": "#FFB039",
    "Quantity Limit": "#2CB339",
    "All Other": "#6B7580",
  },
  measures: [
    { key: "rejections", title: "Rejections" },
    { key: "abandonments", title: "Abandonments" },
  ],
  xAxes: [
    { key: "channel", label: "Channel", axisLabel: "Channel", groupBy: "channel", order: "alpha" },
    { key: "territory", label: "Territory", axisLabel: "Territory", groupBy: "territory", order: "alpha" },
    { key: "period", label: "Quarter", axisLabel: "Quarter", groupBy: "period", order: { by: "period_dt" } },
  ],
  xAxis: "channel",
  yAxis: "pct",
  yAxes: [
    { key: "pct", label: "% by reason", axisTitle: "% of Claims" },
    { key: "abs", label: "No. of claims", axisTitle: "Claims" },
  ],
  unit: "claims",
};
